import { useState, useCallback } from "react";
import {
	WidgetItem,
	CustomTab,
	InsightCategoryEnum,
	InsightEventGroupEnum,
	CUSTOM_GROUP_KEY,
	MapType,
	EventOffensiveEnum,
	getDefaultWidgetSettings,
} from "../constants";
import { groupWidgetsByCategory } from "../functions";
import { UserWidgetSettingsResponse, type saveUserWidgetSettingsRequestBody } from "../../../types";
import { _ } from "i18n";

interface UseWidgetManagementProps {
	userWidgetSettingsData?: UserWidgetSettingsResponse | null;
	handleSaveWidgetSettings?: (insightDisplaySettings: saveUserWidgetSettingsRequestBody) => Promise<void>;
	toastMessage: (type: string, message: string) => void;
}

export const useWidgetManagement = ({
	userWidgetSettingsData,
	handleSaveWidgetSettings,
	toastMessage,
}: UseWidgetManagementProps) => {
	const [activeWidgets, setActiveWidgets] = useState<WidgetItem[]>([]);
	const [userWidgetSettings, setUserWidgetSettings] = useState<any>(null);
	const [customWidgets, setCustomWidgets] = useState<WidgetItem[]>([]);
	const [userCustomTabs, setUserCustomTabs] = useState<CustomTab[]>([]);
	const [customTabs, setCustomTabs] = useState<CustomTab[]>([]);
	const [isSavingView, setIsSavingView] = useState(false);

	const updateWidgetView = useCallback((widgetSettings: any) => {
		let baseWidgets: WidgetItem[] = [];
		if (widgetSettings?.insightDisplaySettings) {
			const customWidgetsSettings: WidgetItem[] = [];
			Object.keys(widgetSettings.insightDisplaySettings).forEach((category) => {
				if (category.includes(CUSTOM_GROUP_KEY)) {
					const customWidgets = widgetSettings?.insightDisplaySettings[category as InsightEventGroupEnum] || [];
					customWidgetsSettings.push(...customWidgets);
					return true;
				}

				const currentTabWidgets = widgetSettings?.insightDisplaySettings[category as InsightCategoryEnum];
				baseWidgets.push(...currentTabWidgets);
			});

			if (customWidgetsSettings.length > 0) {
				const userCustomTabsSettings: CustomTab[] = [];
				customWidgetsSettings.forEach((widget) => {
					baseWidgets.push(widget);
					userCustomTabsSettings.push({
						key: widget.group,
						label: widget.label,
					});
				});
				setUserCustomTabs(userCustomTabsSettings);
			}
			setActiveWidgets(baseWidgets);
		}
	}, []);

	const setDefaultWidgets = useCallback(
		(reset: boolean = false) => {
			let baseWidgets: WidgetItem[] = [];
			const defaultWidgetSettings = getDefaultWidgetSettings();

			if (!userWidgetSettings?.insightDisplaySettings) {
				Object.keys(defaultWidgetSettings).forEach((category) => {
					const currentTabWidgets = defaultWidgetSettings[category as InsightCategoryEnum];
					baseWidgets.push(...currentTabWidgets);
				});

				const mergedWidgets = reset ? [...baseWidgets] : [...baseWidgets, ...customWidgets];
				const groupedWidgets = groupWidgetsByCategory(mergedWidgets);
				setUserWidgetSettings({ insightDisplaySettings: groupedWidgets });
				setActiveWidgets(mergedWidgets);
				updateWidgetView({ insightDisplaySettings: groupedWidgets });
			} else if (reset && userWidgetSettings?.insightDisplaySettings) {
				Object.keys(defaultWidgetSettings).forEach((category) => {
					const currentTabWidgets = defaultWidgetSettings[category as InsightCategoryEnum];
					baseWidgets.push(...currentTabWidgets);
				});

				const customGroupWidgets: WidgetItem[] = [];
				Object.keys(userWidgetSettings?.insightDisplaySettings).forEach((category) => {
					if (category.includes(CUSTOM_GROUP_KEY)) {
						const customWidgets = userWidgetSettings?.insightDisplaySettings[category as InsightEventGroupEnum] || [];
						if (customWidgets?.length) {
							customGroupWidgets.push(...customWidgets);
						}
					}
				});

				const mergedWidgets = [...baseWidgets, ...customGroupWidgets];
				const groupedWidgets = groupWidgetsByCategory(mergedWidgets);
				setUserWidgetSettings({ insightDisplaySettings: groupedWidgets });
				setActiveWidgets(mergedWidgets);
				updateWidgetView({ insightDisplaySettings: groupedWidgets });
			}
		},
		[customWidgets, userWidgetSettings, updateWidgetView]
	);

	const handleDeleteWidget = useCallback(
		(identifier: string | number) => {
			setActiveWidgets((prev) => prev.filter((w) => w.key !== String(identifier)));
			toastMessage("success", _("gamePage.dataInsights.modal.widgetRemoved"));
		},
		[toastMessage]
	);

	const handleMapTypeChange = useCallback((identifier: string | number, newMapType: MapType) => {
		setActiveWidgets((prev) => prev.map((w) => (w.key === identifier ? { ...w, mapType: newMapType } : w)));
	}, []);

	const handleSaveView = useCallback(async () => {
		setIsSavingView(true);
		const payload = {
			offensive: activeWidgets.filter((widget) => widget.category === InsightCategoryEnum.Offensive && !widget.isCustom),
			defensive: activeWidgets.filter((widget) => widget.category === InsightCategoryEnum.Defensive && !widget.isCustom),
			distribution: activeWidgets.filter((widget) => widget.category === InsightCategoryEnum.Distribution && !widget.isCustom),
			goalkeeper: activeWidgets.filter((widget) => widget.category === InsightCategoryEnum.Goalkeeper && !widget.isCustom),
		};

		let customWidgetGroups: any[] = [];
		activeWidgets.forEach((widget) => {
			if (widget.isCustom) {
				customWidgetGroups.push(widget.group);
			}
		});

		if (customWidgetGroups.length > 0) {
			customWidgetGroups = [...new Set([...customWidgetGroups])];
			customWidgetGroups.forEach((group) => {
				payload[group as keyof typeof payload] = activeWidgets.filter((widget) => widget.group === group && widget.isCustom);
			});
		}

		customTabs.forEach((tab) => {
			if (!payload[tab.key as keyof typeof payload]) {
					payload[tab.key as keyof typeof payload] = [
					{
						isPlaceholder: true,
						group: tab.key as InsightEventGroupEnum,
						label: tab.label,
						eventType: EventOffensiveEnum.TakeOn,
						category: InsightCategoryEnum.Offensive,
						parameters: [],
						key: tab.key as string,
						isCustom: true,
						mapType: MapType.PointMap,
					},
				];
			}
		});

		try {
			await handleSaveWidgetSettings?.({ insightDisplaySettings: payload });
		} catch (error) {
			toastMessage("error", _("gamePage.dataInsights.label.unkownError"));
		}

		setIsSavingView(false);
	}, [activeWidgets, customTabs, handleSaveWidgetSettings, toastMessage]);

	return {
		activeWidgets,
		setActiveWidgets,
		userWidgetSettings,
		setUserWidgetSettings,
		customWidgets,
		setCustomWidgets,
		userCustomTabs,
		setUserCustomTabs,
		customTabs,
		setCustomTabs,
		isSavingView,
		updateWidgetView,
		setDefaultWidgets,
		handleDeleteWidget,
		handleMapTypeChange,
		handleSaveView,
	};
};
